import React from 'react';
import { useEntityModal } from '../../contexts/EntityModalContext';
import { ModalZIndexProvider } from '../../contexts/ModalZIndexContext';
import PersonaDetailModal from '../product/PersonaDetailModal';
import GoalDetailModal from '../goal/GoalDetailModal';
import FeatureDetailModal from '../product/FeatureDetail';
import EpicDetailModal from '../epic/EpicDetailModal';
import InitiativeDetailModal from '../initiative/InitiativeDetailModal';
import RequestEditModal from '../requests/RequestEditModal';
import SegmentDetailModal from '../product/SegmentDetailModal';

/**
 * EntityModals - Renders modals based on EntityModalContext state
 * 
 * Place this component inside EntityModalProvider to render
 * entity detail modals when triggered from co-pilot entity links.
 * 
 * When a parent modal has registered callbacks (parentCallbacksRef),
 * child modals receive them as parentRefresh so they can refresh
 * the parent's local state directly, avoiding full page refreshes.
 */
const EntityModals = ({ 
  productId, 
  refresh,
  markStale,
  personas = [],
  epics = [],
  goals = [],
  allFeatures = [],
  productContext = {},
  onSaveSegment,
  onDeleteSegment,
  canEdit = true,
}) => {
  const { selectedEntity, closeEntityModal, parentCallbacksRef } = useEntityModal();
  
  if (!selectedEntity) return null;
  
  const { type, data, zIndex = 50 } = selectedEntity;
  
  // Check if a parent modal has registered callbacks for this entity type.
  // If so, the child uses parentRefresh instead of page-level onRefresh.
  const parentCallbacks = parentCallbacksRef?.current;
  const isHandledByParent = parentCallbacks && parentCallbacks[type];
  
  // Suppress child modals only when a parent modal is open (Level 1+).
  // Collection co-pilot links open Level 0 modals with full interactivity.
  const hasParent = !!parentCallbacks;
  
  // Map entity type → collection name for optimistic refresh
  const typeToCollection = { feature: 'features', epic: 'epics', initiative: 'initiatives', goal: 'goals', persona: 'personas' };
  
  const handleSaveAndClose = (savedData) => {
    closeEntityModal();
    if (!isHandledByParent) {
      const collection = typeToCollection[type];
      if (collection && savedData && typeof savedData === 'object') {
        refresh?.('entity', collection, savedData);
      } else {
        refresh?.('product');
      }
    }
  };
  
  const handleDeleteAndClose = () => {
    closeEntityModal();
    const collection = typeToCollection[type];
    if (collection) {
      refresh?.('collection', collection);
    } else {
      refresh?.('product');
    }
  };
  
  let modalContent = null;
  
  switch (type) {
    case 'feature':
      modalContent = (
        <FeatureDetailModal
          isOpen={true}
          onClose={closeEntityModal}
          feature={data}
          productId={productId}
          parentRefresh={isHandledByParent ? parentCallbacks : undefined}
          onSave={handleSaveAndClose}
          onDelete={handleDeleteAndClose}
          personas={personas}
          productContext={productContext}
          epics={epics}
          zIndex={zIndex}
          suppressChildModals={hasParent}
        />
      );
      break;
      
    case 'epic':
      modalContent = (
        <EpicDetailModal
          isOpen={true}
          onClose={closeEntityModal}
          epic={data}
          productId={productId}
          parentRefresh={isHandledByParent ? parentCallbacks : undefined}
          onSave={handleSaveAndClose}
          productContext={productContext}
          personas={personas}
          zIndex={zIndex}
          suppressChildModals={hasParent}
        />
      );
      break;
      
    case 'persona':
      modalContent = (
        <PersonaDetailModal
          isOpen={true}
          onClose={closeEntityModal}
          persona={data}
          productId={productId}
          productContext={productContext}
          allFeatures={allFeatures}
          allEpics={epics}
          onUpdate={() => {
            closeEntityModal();
            onRefresh?.();
          }}
          zIndex={zIndex}
        />
      );
      break;
      
    case 'goal':
      modalContent = (
        <GoalDetailModal
          isOpen={true}
          onClose={closeEntityModal}
          goal={data}
          productId={productId}
          parentRefresh={isHandledByParent ? parentCallbacks : undefined}
          onSave={handleSaveAndClose}
          zIndex={zIndex}
          suppressChildModals={hasParent}
        />
      );
      break;
      
    case 'request':
      modalContent = (
        <RequestEditModal
          isOpen={true}
          onClose={closeEntityModal}
          request={data}
          productId={productId}
          onSave={handleSaveAndClose}
          markStale={markStale}
          zIndex={zIndex}
        />
      );
      break;
    
    case 'initiative':
      modalContent = (
        <InitiativeDetailModal
          isOpen={true}
          onClose={closeEntityModal}
          initiative={data}
          productId={productId}
          goals={goals}
          personas={personas}
          epics={epics}
          productContext={productContext}
          parentRefresh={isHandledByParent ? parentCallbacks : undefined}
          onSave={handleSaveAndClose}
          onDelete={handleDeleteAndClose}
          zIndex={zIndex}
          suppressChildModals={hasParent}
        />
      );
      break;
      
    case 'segment':
      modalContent = (
        <SegmentDetailModal
          isOpen={true}
          segment={data}
          productId={data?.product_id || productId}
          onSave={async (segmentData) => {
            await onSaveSegment?.(segmentData);
            closeEntityModal();
            refresh?.('product');
          }}
          onDelete={async (segmentId) => {
            await onDeleteSegment?.(segmentId);
            closeEntityModal();
            refresh?.('product');
          }}
          onClose={closeEntityModal}
          canEdit={canEdit}
          zIndex={zIndex}
        />
      );
      break;
      
    default:
      return null;
  }
  
  return (
    <ModalZIndexProvider zIndex={zIndex}>
      {modalContent}
    </ModalZIndexProvider>
  );
};

export default EntityModals;
